import { FC, useEffect, useState } from 'react';

import { usePage } from '@inertiajs/react';
import { ChevronRight } from 'lucide-react';

import {
    PremiumOffersSection,
    premiumOffersActionButtonClass,
} from './PremiumOffersSection';
import { Button } from '../../components/ui/Button';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '../../components/ui/Tooltip';
import { premiumOffersRoute, Routes } from '../../models/routes';
import { Expansion, Offer, ProductType, Universe } from '../../types';
import { cn } from '../../utils';

type PageProps = {
    premiumOffersPreview: Offer[] | null;
    currentProductType: ProductType | null;
    currentExpansion: Expansion | null;
    currentUniverse: Universe;
};

const PREMIUM_OFFERS_PREVIEW_PAGE_SIZE = 6;

export const PremiumOffersPreview: FC = () => {
    const { url } = usePage();
    const {
        premiumOffersPreview,
        currentProductType,
        currentExpansion,
        currentUniverse,
    } = usePage<PageProps>().props;
    const currentPath = url.split('?')[0];
    const isUniverseRoot =
        currentPath === Routes.Home || currentPath === currentUniverse.url_path;
    const premiumOffers = premiumOffersPreview ?? [];
    const [visibleStartIndex, setVisibleStartIndex] = useState(0);
    const visiblePremiumOffers = premiumOffers.slice(
        visibleStartIndex,
        visibleStartIndex + PREMIUM_OFFERS_PREVIEW_PAGE_SIZE,
    );
    const hasNextPremiumOffers =
        visibleStartIndex + PREMIUM_OFFERS_PREVIEW_PAGE_SIZE <
        premiumOffers.length;

    useEffect(() => {
        setVisibleStartIndex(0);
    }, [currentUniverse.slug]);

    const showNextPremiumOffers = () => {
        setVisibleStartIndex((currentIndex) => {
            const nextIndex = currentIndex + PREMIUM_OFFERS_PREVIEW_PAGE_SIZE;

            return nextIndex < premiumOffers.length ? nextIndex : currentIndex;
        });
    };

    if (!isUniverseRoot || currentProductType || currentExpansion) {
        return null;
    }

    if (!premiumOffers.length) {
        return null;
    }

    return (
        <PremiumOffersSection
            offers={visiblePremiumOffers}
            description=""
            trailingControl={
                hasNextPremiumOffers ? (
                    <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                            <Button
                                type="button"
                                variant="outline"
                                className={cn(
                                    'h-11 w-11 rounded-full p-0 shadow-lg',
                                    premiumOffersActionButtonClass,
                                )}
                                onClick={showNextPremiumOffers}
                                data-testid="premium-offers-preview-next"
                                aria-label="Показати наступні преміум пропозиції"
                            >
                                <ChevronRight className="h-5 w-5" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="left">
                            Наступні 6 преміум пропозицій
                        </TooltipContent>
                    </Tooltip>
                ) : null
            }
            viewAllHref={premiumOffersRoute(currentUniverse.slug)}
            viewAllLabel="Усі преміум пропозиції"
            largeScreenOfferLimit={PREMIUM_OFFERS_PREVIEW_PAGE_SIZE}
        />
    );
};
